import { AlertCircle } from '@chain-zen/ui'
import { differenceInHours, parseISO } from 'date-fns'

import type { IDeviceInfo, TLocationSettingsType, TMotionActivityType } from '../../../../types/tracking'
import { AlertMessage } from '../alert-message'
import { messages } from '../constants'
import { actionRequiredMessage, formatDate, isMoreThanEightHoursAgo, isNotMovingOnIPhone } from './utils'

interface AlertSectionProps {
  isTrackingOn?: boolean
  incorrectAuthorization: boolean
  isHistoricalDevice: boolean
  lastSeen: string | null
  locationSettings: TLocationSettingsType | null
  device: IDeviceInfo | null
  lastMotionActivity: TMotionActivityType | null
}

export const AlertSection = ({
  isTrackingOn,
  incorrectAuthorization,
  isHistoricalDevice,
  lastSeen,
  locationSettings,
  device,
  lastMotionActivity
}: AlertSectionProps) => {
  if (isHistoricalDevice) return null

  const showTrackingOffAlert = !isTrackingOn && !incorrectAuthorization
  const showAuthorizationAlert = !!incorrectAuthorization
  const showDeviceNotDetectedAlert = isMoreThanEightHoursAgo(lastSeen) && isTrackingOn && !incorrectAuthorization
  const showStationaryAlert =
    isNotMovingOnIPhone({ device, motionActivity: lastMotionActivity, utcDateString: lastSeen }) &&
    !incorrectAuthorization

  return (
    <>
      {!!showTrackingOffAlert && (
        <AlertMessage
          alertIcon={AlertCircle}
          alertMessage="The driver has turned the tracking off. Please call or message the driver and ask them to turn on the tracking on using the button in the upper right hand corner in the App"
          alertTitle="Action required"
          textAndIconColor="orange"
        />
      )}

      {!!showAuthorizationAlert && (
        <AlertMessage
          alertIcon={AlertCircle}
          alertMessage={actionRequiredMessage(locationSettings?.authorizationStatus)}
          alertTitle="Action required"
          textAndIconColor="orange"
        />
      )}

      {showDeviceNotDetectedAlert ? (
        <AlertMessage
          alertIcon={AlertCircle}
          alertMessage={`The driver's device has not been detected since ${formatDate(
            lastSeen as string
          )}. Either the driver has had their phone off/has been in a low reception area for the past ${differenceInHours(
            new Date(),
            parseISO(lastSeen as string)
          )} hours, or they deleted the app. Please call the driver and ask them to open the app, which will reactivate tracking.`}
          alertTitle="Device not detected"
          textAndIconColor="red"
        />
      ) : (
        showStationaryAlert && (
          <AlertMessage
            alertIcon={AlertCircle}
            alertMessage={messages.STATIONARY_DEVICE_ALERT_MESSAGE}
            alertTitle="Stationary device"
            textAndIconColor="purple"
          />
        )
      )}
    </>
  )
}
